import i18next from 'i18next';
import { isPushActive } from './pushSubscription';

// With browser push on, the push is the notification while this tab is not
// focused (the service worker shows it, tab open or not), and the tab raises
// its own only while focused, when the service worker stays silent. Without
// push the tab is the only source, focused or not.
const tabShouldNotify = () => !isPushActive() || document.hasFocus();

// The same look as a push (sw-template.js): the app icon, and on Android the
// Lixani L as the small icon instead of Chrome's. The badge is a white
// silhouette on transparent; Android tints it, so only its shape counts.
const NOTIFICATION_ICON = '/android-chrome-192x192.png';
const NOTIFICATION_BADGE = '/notification-badge.png';

// Helper function to play notification sound
const playNotificationSound = (soundPath) => {
  try {
    const audio = new Audio(soundPath);
    audio.volume = 0.7; // Set to 70% volume
    audio.play().catch((error) => {
      console.warn('Sound playback failed:', error);
    });
  } catch (error) {
    console.warn('Error creating audio:', error);
  }
};

// Chat notifications all share one tag, so a new message replaces the previous
// one instead of stacking. A single timer therefore covers "the chat
// notification", and each new message restarts its clock.
const CHAT_NOTIFICATION_TIMEOUT_MS = 20000;
let chatNotificationTimer = null;

const cancelChatNotificationClose = () => {
  if (chatNotificationTimer) {
    clearTimeout(chatNotificationTimer);
    chatNotificationTimer = null;
  }
};

const scheduleChatNotificationClose = (close) => {
  cancelChatNotificationClose();
  chatNotificationTimer = setTimeout(() => {
    chatNotificationTimer = null;
    try {
      close();
    } catch (error) {
      // Silently ignore if the notification is already gone
    }
  }, CHAT_NOTIFICATION_TIMEOUT_MS);
};

export const requestNotificationPermission = async () => {
  if ('Notification' in window && Notification.permission === 'default') {
    return await Notification.requestPermission();
  }
  return Notification.permission;
};

// Set up service worker message listener for notification clicks
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data.type === 'notification-click') {
      // A push's notification: no click handler of this tab's is waiting
      // for it, so it is opened by what it names (usePushNotificationClicks).
      if (event.data.fromPush) {
        if (window.__pushNotificationClickHandler) {
          window.__pushNotificationClickHandler(event.data);
        }
        return;
      }
      if (event.data.notificationType === 'chat-message') {
        cancelChatNotificationClose();
        if (window.__chatNotificationClickHandler) {
          window.__chatNotificationClickHandler();
        }
      } else if (event.data.notificationType === 'video-call') {
        if (window.__videoCallNotificationClickHandler) {
          window.__videoCallNotificationClickHandler();
        }
      } else if (event.data.notificationType === 'email-message') {
        if (window.__emailNotificationClickHandler) {
          window.__emailNotificationClickHandler();
        }
      } else if (event.data.notificationType === 'meeting') {
        if (window.__meetingNotificationClickHandler) {
          window.__meetingNotificationClickHandler();
        }
      }
    }
  });
}

export const showChatNotification = async (message, onNotificationClick) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }
  if (!tabShouldNotify()) return;

  try {
    // Try Service Worker API first (required for mobile)
    if ('serviceWorker' in navigator) {
      try {
        // Get existing registration
        const registration = await navigator.serviceWorker.getRegistration();

        // If we have a registration with an active worker, show notification
        if (registration && registration.active) {
          // Store click handler globally for service worker notifications
          window.__chatNotificationClickHandler = onNotificationClick;

          await registration.showNotification('Lixani', {
            body: `${message.senderFirstName}: ${message.message}`,
            icon: NOTIFICATION_ICON,
            badge: NOTIFICATION_BADGE,
            tag: 'chat-message',
            requireInteraction: true,
            vibrate: [200, 100, 200],
            data: {
              type: 'chat-message',
              threadId: message.threadId,
              url: window.location.origin,
            },
          });

          // requireInteraction stops the browser dropping this after its own
          // few seconds, so close it ourselves once it has had time to be read.
          scheduleChatNotificationClose(() => {
            registration
              .getNotifications({ tag: 'chat-message' })
              .then((shown) => shown.forEach((n) => n.close()))
              .catch(() => {});
          });
          return;
        }
      } catch (swError) {
        // Service Worker failed, try fallback
      }
    }

    // Desktop fallback - try regular Notification API
    try {
      const notification = new Notification('Lixani', {
        body: `${message.senderFirstName}: ${message.message}`,
        icon: NOTIFICATION_ICON,
        badge: NOTIFICATION_BADGE,
        tag: 'chat-message',
        requireInteraction: true,
      });

      scheduleChatNotificationClose(() => notification.close());

      notification.onclick = () => {
        cancelChatNotificationClose();
        if (onNotificationClick) {
          onNotificationClick();
        }
        window.focus();
        notification.close();
      };
    } catch (constructorError) {
      // Silently ignore if Notification constructor fails
    }
  } catch (error) {
    // Silently ignore all notification errors
  }
};

// Call notifications the page raised without the service worker, by call, so
// closeCallNotification can reach them too.
const fallbackCallNotifications = new Map();

export const showVideoCallNotification = async (
  body,
  onNotificationClick,
  callId
) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }
  if (!tabShouldNotify()) return;

  try {
    // Try Service Worker API first (required for mobile)
    if ('serviceWorker' in navigator) {
      try {
        // Get existing registration
        const registration = await navigator.serviceWorker.getRegistration();

        // If we have a registration with an active worker, show notification
        if (registration && registration.active) {
          // Store click handler globally for service worker notifications
          window.__videoCallNotificationClickHandler = onNotificationClick;

          await registration.showNotification('Lixani', {
            body,
            icon: NOTIFICATION_ICON,
            badge: NOTIFICATION_BADGE,
            tag: 'video-call',
            requireInteraction: true,
            vibrate: [300, 100, 300, 100, 300],
            data: {
              type: 'video-call',
              callId,
              url: window.location.origin,
            },
          });
          return;
        }
      } catch (swError) {
        // Service Worker failed, try fallback
      }
    }

    // Desktop fallback - try regular Notification API
    try {
      const notification = new Notification('Lixani', {
        body,
        icon: NOTIFICATION_ICON,
        badge: NOTIFICATION_BADGE,
        tag: 'video-call',
        requireInteraction: true,
      });
      if (callId) fallbackCallNotifications.set(callId, notification);

      notification.onclick = () => {
        if (onNotificationClick) {
          onNotificationClick();
        }
        window.focus();
        notification.close();
      };
    } catch (constructorError) {
      // Silently ignore if Notification constructor fails
    }
  } catch (error) {
    // Silently ignore all notification errors
  }
};

// This device's "X is calling" notifications for one call: raised by the page
// or by a push, they share the tag and carry the call's id.
const callNotificationsOf = async (callId) => {
  const registration =
    'serviceWorker' in navigator
      ? await navigator.serviceWorker.getRegistration()
      : null;
  if (!registration || !registration.getNotifications) return [];
  const shown = await registration.getNotifications({ tag: 'video-call' });
  return shown.filter(
    (notification) =>
      !notification.data?.callId || notification.data.callId === callId
  );
};

/**
 * Take a call's notification away on this device: the call was answered or
 * opened here, or put off with "not now". It would otherwise go on saying
 * "is calling" (the push version stays until touched).
 */
export const closeCallNotification = async (callId) => {
  try {
    const fallback = fallbackCallNotifications.get(callId);
    if (fallback) {
      fallback.close();
      fallbackCallNotifications.delete(callId);
    }
    (await callNotificationsOf(callId)).forEach((notification) =>
      notification.close()
    );
  } catch (error) {
    // Nothing to close, or no access: the notification stays as it was.
  }
};

/**
 * Turn a call's notification on this device into a quiet "missed call", if
 * one is still showing. Only for a tab that raised it itself: with push on,
 * the server sends the same replacement to every device.
 */
export const showMissedCallNotification = async (callId, body, threadId) => {
  try {
    const fallback = fallbackCallNotifications.get(callId);
    if (fallback) {
      fallback.close();
      fallbackCallNotifications.delete(callId);
    }
    const registration =
      'serviceWorker' in navigator
        ? await navigator.serviceWorker.getRegistration()
        : null;
    if (!registration || (await callNotificationsOf(callId)).length === 0) {
      return;
    }
    await registration.showNotification('Lixani', {
      body,
      icon: NOTIFICATION_ICON,
      badge: NOTIFICATION_BADGE,
      tag: 'video-call',
      renotify: false,
      silent: true,
      data: {
        type: 'missed-call',
        callId,
        threadId,
        url: window.location.origin,
        fromPush: true,
        openUrl: threadId
          ? `/?chatThread=${encodeURIComponent(threadId)}`
          : undefined,
      },
    });
  } catch (error) {
    // The old notification stays as it was.
  }
};

export const showMeetingNotification = async (
  body,
  onNotificationClick,
  tag = 'meeting'
) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }

  try {
    // Try Service Worker API first (required for mobile)
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration();

        if (registration && registration.active) {
          // Store click handler globally for service worker notifications
          window.__meetingNotificationClickHandler = onNotificationClick;

          await registration.showNotification('Lixani', {
            body,
            icon: NOTIFICATION_ICON,
            badge: NOTIFICATION_BADGE,
            // Per meeting + phase, so the 15-min and start reminders don't
            // replace each other and different meetings don't collide.
            tag,
            requireInteraction: true,
            vibrate: [200, 100, 200],
            data: {
              type: 'meeting',
              url: window.location.origin,
            },
          });
          return;
        }
      } catch (swError) {
        // Service Worker failed, try fallback
      }
    }

    // Desktop fallback - try regular Notification API
    try {
      const notification = new Notification('Lixani', {
        body,
        icon: NOTIFICATION_ICON,
        badge: NOTIFICATION_BADGE,
        tag,
        requireInteraction: true,
      });

      notification.onclick = () => {
        if (onNotificationClick) {
          onNotificationClick();
        }
        window.focus();
        notification.close();
      };
    } catch (constructorError) {
      // Silently ignore if Notification constructor fails
    }
  } catch (error) {
    // Silently ignore all notification errors
  }
};

export const showEmailNotification = async (
  emailMessage,
  onNotificationClick
) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }

  // Play notification sound
  playNotificationSound('/sounds/email-incoming.mp3');

  // The sound is the tab's alone; the notification may be the push's.
  if (!tabShouldNotify()) return;

  try {
    // Parse from address - it's an object with an 'address' property
    const fromAddress =
      emailMessage.from?.address ||
      i18next.t('projectEmail.from', 'Unknown sender');
    const subject =
      emailMessage.subject || i18next.t('projectEmail.subject', 'No subject');
    const title = i18next.t(
      'projectEmail.new_email_notification',
      'New Email - Lixani'
    );
    const bodyText = `${i18next.t('projectEmail.from', 'From')}: ${fromAddress}\n${subject}`;

    // Try Service Worker API first (required for mobile)
    if ('serviceWorker' in navigator) {
      try {
        // Get existing registration
        const registration = await navigator.serviceWorker.getRegistration();

        // If we have a registration with an active worker, show notification
        if (registration && registration.active) {
          // Store click handler globally for service worker notifications
          window.__emailNotificationClickHandler = onNotificationClick;

          await registration.showNotification(title, {
            body: bodyText,
            icon: NOTIFICATION_ICON,
            badge: NOTIFICATION_BADGE,
            tag: 'email-message',
            requireInteraction: false,
            vibrate: [200, 100, 200],
            silent: true,
            data: {
              type: 'email-message',
              emailId: emailMessage.id,
              projectKey: emailMessage.projectKey,
              url: window.location.origin,
            },
          });
          return;
        }
      } catch (swError) {
        // Service Worker failed, try fallback
      }
    }

    // Desktop fallback - try regular Notification API
    try {
      const notification = new Notification(title, {
        body: bodyText,
        icon: NOTIFICATION_ICON,
        badge: NOTIFICATION_BADGE,
        tag: 'email-message',
        requireInteraction: false,
        silent: true,
      });

      notification.onclick = () => {
        if (onNotificationClick) {
          onNotificationClick();
        }
        window.focus();
        notification.close();
      };
    } catch (constructorError) {
      // Silently ignore if Notification constructor fails
    }
  } catch (error) {
    // Silently ignore all notification errors
  }
};
